// Password rules, in the same shape as validateReflectionBody() in index.js: return an error
// string, or null when the value is acceptable.
//
// This file is CommonJS but is imported by the React app as well as the API (both trees live
// under src/, so CRA's module scope allows it and webpack handles the require/exports). That is
// deliberate: if the client and server disagreed about what counts as a symbol, the form would
// accept a password the server then rejects, and the user would have no way to tell why.
//
// Deliberately absent: a maximum on repeated characters, a dictionary check, and forced
// rotation. They push people towards writing passwords down without measurably helping.

const MIN_LENGTH = 14
// Not a security limit — an upper bound on scrypt's work. Hashing is deliberately expensive, so
// an unbounded password length is a way to make the server do unbounded work per request.
const MAX_LENGTH = 200

// "Symbol" is defined by exclusion rather than as a whitelist, so spaces, accented letters and
// emoji all count. A whitelist only ever provokes arguments about which punctuation is allowed.
const CLASSES = [
    { label: 'a lower-case letter', pattern: /[a-z]/ },
    { label: 'an upper-case letter', pattern: /[A-Z]/ },
    { label: 'a number', pattern: /[0-9]/ },
    { label: 'a symbol', pattern: /[^A-Za-z0-9]/ },
]

// Shown in the sign-up form before the user types. Requirements a user discovers only by
// failing are requirements they resent.
const REQUIREMENTS = [`at least ${MIN_LENGTH} characters`, ...CLASSES.map(c => c.label)]

const formatList = items => {
    if (items.length === 1) return items[0]
    return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
}

// Reports every missing class at once. Returning only the first turns setting a password into a
// guessing game played one round-trip at a time.
const validatePassword = password => {
    if (typeof password !== 'string' || password === '') return 'password is required'
    if (password.length < MIN_LENGTH) return `password must be at least ${MIN_LENGTH} characters`
    if (password.length > MAX_LENGTH) return `password must be at most ${MAX_LENGTH} characters`
    const missing = CLASSES.filter(c => !c.pattern.test(password)).map(c => c.label)
    if (missing.length > 0) return `password must include ${formatList(missing)}`
    return null
}

module.exports = { validatePassword, REQUIREMENTS, MIN_LENGTH, MAX_LENGTH }
